import { useCallback, useEffect, useState } from "react";

export type PresentationMode = "free" | "locked";

export interface PresentationView {
  fullscreen: boolean;
  /** `free` = reading/scrolling, nothing dimmed. `locked` = one unit focused. */
  mode: PresentationMode;
  /** The focused unit's stable anchor id, or null. */
  focusedAnchor: string | null;
  /** The focused unit's CURRENT index (derived from the anchor), or null. */
  focusedUnit: number | null;
  /** The focused unit's history is unlocked for text edits / deletes. */
  unlocked: boolean;
  /** The unlocked unit's anchor, or null. */
  unlockedAnchor: string | null;
  /** Toggle the unlock on the focused unit. No-op unless `mode === "locked"`. */
  toggleUnlock: () => void;
  enter: () => void;
  exit: () => void;
  toggleFullscreen: () => void;
  /** Lock a specific unit (focus-follows-add / click-to-edit). */
  focusUnitAt: (index: number) => void;
  /** Drop the lock — free browsing, dim removed. */
  release: () => void;
  /** Handle a key while presenting. `nearestUnit` is measured by the caller
   * (`pickNearestUnit`: at the top of the Protokoll the first unit, otherwise the
   * one with the largest visible area); it is only used when locking from `free`.
   * Returns true when the key was consumed. */
  handleKey: (key: string, nearestUnit: number) => boolean;
}

/**
 * Single-level presentation cursor over the memo's focus units
 * (Zwischenabschnitte, falling back to whole Abschnitte).
 *   - ↑/← previous unit, ↓/→ next unit (locked mode)
 *   - a cursor key while `free` grabs the unit you are already reading
 *   - mouse wheel / touch scroll calls `release()` → back to `free`
 *   - Esc unlocks, then exits.
 *
 * Focus is stored as the unit's ANCHOR ID, not its index: the memo polls every
 * 5s, so a co-editor inserting/deleting/reordering a unit above the focus would
 * otherwise silently move the spotlight (and smooth-scroll the shared screen) to
 * a different Zwischenabschnitt. The index is derived from the anchor each render.
 *
 * Purely client-side; the presenter screen-shares via Teams.
 */
export function usePresentationView(anchors: string[]): PresentationView {
  const [fullscreen, setFullscreen] = useState(false);
  const [mode, setMode] = useState<PresentationMode>("free");
  const [focusedAnchor, setFocusedAnchor] = useState<string | null>(null);

  const unitCount = anchors.length;
  const idx = focusedAnchor ? anchors.indexOf(focusedAnchor) : -1;
  const focusedUnit = idx >= 0 ? idx : null;

  const clamp = useCallback((i: number) => Math.max(0, Math.min(i, unitCount - 1)), [unitCount]);

  // Editing history on the focused unit is opt-in and must never outlive that
  // focus. Deriving `unlocked` from the anchors is NOT enough on its own: unlock
  // u2, step to u3, step back to u2 — a stale anchor would silently re-arm
  // editing. So the anchor is cleared the instant the focus moves.
  const [unlockAnchor, setUnlockAnchor] = useState<string | null>(null);
  useEffect(() => {
    if (unlockAnchor !== null && unlockAnchor !== focusedAnchor) setUnlockAnchor(null);
  }, [focusedAnchor, unlockAnchor]);

  const unlocked = mode === "locked" && unlockAnchor !== null && unlockAnchor === focusedAnchor;

  const toggleUnlock = useCallback(() => {
    if (mode !== "locked" || focusedAnchor === null) return;
    setUnlockAnchor((cur) => (cur === focusedAnchor ? null : focusedAnchor));
  }, [mode, focusedAnchor]);

  const release = useCallback(() => {
    setMode("free");
    setFocusedAnchor(null);
    setUnlockAnchor(null);
  }, []);

  // The focused unit vanished (its entry/block was deleted) — never strand a dim.
  useEffect(() => {
    if (focusedAnchor !== null && !anchors.includes(focusedAnchor)) {
      setFocusedAnchor(null);
      setMode("free");
    }
  }, [anchors, focusedAnchor]);

  const enter = useCallback(() => {
    setFullscreen(true);
    setMode("free");
    setFocusedAnchor(null);
  }, []);

  const exit = useCallback(() => {
    setFullscreen(false);
    setMode("free");
    setFocusedAnchor(null);
    setUnlockAnchor(null);
  }, []);

  const toggleFullscreen = useCallback(() => {
    // Clear focus alongside the flip so leaving presentation can never strand a
    // dimmed, non-interactive unit in the normal view.
    setFullscreen((v) => !v);
    setMode("free");
    setFocusedAnchor(null);
    setUnlockAnchor(null);
  }, []);

  const focusUnitAt = useCallback(
    (index: number) => {
      if (unitCount === 0) return;
      setFocusedAnchor(anchors[clamp(index)] ?? null);
      setMode("locked");
    },
    [anchors, clamp, unitCount],
  );

  const handleKey = useCallback(
    (key: string, nearestUnit: number): boolean => {
      if (key === "Escape") {
        if (mode === "locked") release();
        else setFullscreen(false);
        return true;
      }
      const back = key === "ArrowUp" || key === "ArrowLeft";
      const forward = key === "ArrowDown" || key === "ArrowRight";
      if (!back && !forward) return false;
      if (unitCount === 0) return true;
      if (mode === "free" || focusedUnit === null) {
        // Grab where the reader already is — do NOT advance.
        setFocusedAnchor(anchors[clamp(nearestUnit)] ?? null);
        setMode("locked");
        return true;
      }
      setFocusedAnchor(anchors[clamp(focusedUnit + (forward ? 1 : -1))] ?? null);
      return true;
    },
    [anchors, clamp, focusedUnit, mode, release, unitCount],
  );

  return {
    fullscreen,
    mode,
    focusedAnchor,
    focusedUnit,
    unlocked,
    unlockedAnchor: unlocked ? focusedAnchor : null,
    toggleUnlock,
    enter,
    exit,
    toggleFullscreen,
    focusUnitAt,
    release,
    handleKey,
  };
}
